import { add, sub } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import _ from 'lodash';
import { Key } from 'antd/lib/table/interface';
import { Button } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageHeader } from 'src/components/PageHeader';
import { CAISO_CMRI_VIEW } from 'src/pages/caiso/interface';
import { usePageLocation } from 'src/pages/usePageState';
import { CaisoBreadcrumbNav } from 'src/components/CaisoBreadcrumb/CaisoBreadcrumbNav';
import { CAISO_TZ } from 'src/domain/caiso';
import { MarketAwardsDaysSelector } from '../components/MarketAwardsDaysSelector/MarketAwardsDaysSelector';
import { useContext, useReducer } from 'react';
import { useCaisoMarketAwardsListQuery } from 'src/domain/caiso/cmri/queries';
import { IMarketAward, MarketAwardStatus } from 'src/domain/caiso/cmri/interface';
import { MarketAwardsDenyModal } from '../components/MarketAwardsDenyModal/MarketAwardsDenyModal';
import { marketAwardsModal, ModalState, reducer } from './reducer';
import { getColumnSortOrder, sortByField } from 'src/components/table/columnFormatHelpers';
import { MarketAwardSourceTag } from '../components/MarketAwardSourceTag';
import { currencyUSDFormatter } from 'src/domain/common/currencyFormatters';
import { AbilityContext } from 'src/components/ability/can';
import DislikeOutlined from '@ant-design/icons/lib/icons/DislikeOutlined';
import LikeOutlined from '@ant-design/icons/lib/icons/LikeOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import { ActionList } from 'src/components/table/actionList/actionList';
import { toUsefulParams } from 'src/pages/toUsefulParams';
import { useTableSelection } from 'src/components/table/useTableSelection';
import { useDocumentTitle } from 'src/components/useDocumentTitle';
import { MarketAwardsViewModal } from '../components/MarketAwardsViewModal/MarketAwardsViewModal';
import { canCombineAndApprove, isMarketAwardExpired, makeUrlForMarketAwardDispatchEvent } from 'src/domain/caiso/cmri/market-award';
import Tooltip from 'antd/lib/tooltip';
import { useAuth } from 'src/domain/auth/useAuth';
import HistoryOutlined from '@ant-design/icons/lib/icons/HistoryOutlined';
import { AuditTrailModal } from 'src/components/modals/AuditTrailModal/AuditTrailModal';
import { AuditEntityType } from 'src/domain/audit-trail/interface';
import { CustomizableTableContextProvider } from 'src/components/table/CustomizableTableContext';
import Layout from 'antd/lib/layout';
import { CustomizableColumnType, CustomizableTable } from 'src/components/table/CustomizableTable';
import { CmriOverviewSidebar } from './sidebar/CmriOverviewSidebar';
import { useCmriOverviewSidebar } from './sidebar/useCmriOverviewSidebar';
import { WithEmptyDataTable } from 'src/components/table/withEmptyDataTable';
import { SidebarGrid } from 'src/components/Sidebar/SidebarGrid';
import './CmriOverviewView.css';

interface CmriOverviewViewProps {
    view: CAISO_CMRI_VIEW;
    handleView: (view: CAISO_CMRI_VIEW) => void;
}

export const CmriOverviewView = ({ view, handleView }: CmriOverviewViewProps) => {
    useDocumentTitle('CAISO CMRI Overview');

    const auth = useAuth()!;
    const location = useLocation();
    const ability = useContext(AbilityContext);
    const navigation = useNavigate();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);

    const { selectedKeys, selectedItems, handleSelectedItems } = useTableSelection('id');

    const initialState: ModalState = { modal: undefined, targets: [] };
    const [modalState, dispatch] = useReducer(reducer, initialState);

    const days = getDateRanges();
    const { data, isFetching } = useCaisoMarketAwardsListQuery({
        start: days.yesterday.start,
        end: days.tomorrow.end,
    });

    const counters = getDaysCounters(data, days, pageState);
    console.info('pageState.filter', pageState.filter);

    // based on current filter and active day tab - filter market awards
    const activeDay = getActiveDay(pageState.tab);

    const marketAwards = applyFilter(data, {
        dateRange: days[activeDay],
        ...(pageState?.filter?.resource && { resource: pageState.filter.resource }),
        ...(pageState?.filter?.status && { status: pageState.filter.status }),
        ...(pageState?.filter?.source && { source: pageState.filter.source }),
        ...(pageState?.filter?.hoursRange && { hoursRange: pageState.filter.hoursRange }),
        ...(pageState?.filter?.mw && { mw: pageState.filter.mw }),
        ...(pageState?.filter?.price && { price: pageState.filter.price }),
        ...(pageState?.filter?.earnings && { earnings: pageState.filter.earnings }),
    });

    console.info('marketAwards filtered', marketAwards);

    const onDayChange = (value: DayValue) => {
        const preparedParams = {
            ...pageState,
            tab: value,
        };
        setPageQuery(preparedParams);
        handleSelectedItems([], []);
    };

    function handleColumnResize(index: number, deltaWidth: number) {
        // setColumns(prevColumns =>
        //     prevColumns.map((col, colIndex) => {
        //         if (colIndex === index) {
        //             const currentWidth = Number(col.width ?? 0);
        //             const newWidth = currentWidth + deltaWidth;
        //             return {
        //                 ...col,
        //                 width: currentWidth < 50 ? 50 : newWidth,
        //             };
        //         }
        //         return col;
        //     })
        // );
    }

    function showApproveModal(record: IMarketAward) {
        return () => {
            dispatch({ type: 'APPROVE', payload: [record] });
        };
    }

    function showDenyModal(record: IMarketAward) {
        return () => {
            dispatch({ type: 'DENY', payload: [record] });
        };
    }

    function showViewModal(record: IMarketAward) {
        return () => {
            dispatch({ type: 'VIEW', payload: record });
        };
    }

    function showAuditModal(record: IMarketAward) {
        return () => {
            dispatch({ type: 'AUDIT', payload: record });
        };
    }

    function goToEvents(record: IMarketAward) {
        return () => {
            if (!record.dispatch?.id) return;

            const eventLink = makeUrlForMarketAwardDispatchEvent(record.dispatch.id);
            navigation(eventLink);
        };
    }

    function onDenySelected() {
        dispatch({ type: 'DENY', payload: [...selectedItems] });
    }

    function onCombineAndApproveSelected() {
        dispatch({ type: 'APPROVE', payload: [...selectedItems] });
    }

    const actions = (record: IMarketAward) => {
        const actions = [];
        const isExpired = isMarketAwardExpired(record);

        switch (record.status) {
            /** IGNORED means expired market awards can be only viewed */
            case MarketAwardStatus.IGNORED:
                actions.push({
                    title: 'View',
                    icon: <EyeOutlined />,
                    onClick: (record: IMarketAward) => showViewModal(record),
                });
                break;
            case MarketAwardStatus.NEW:
                actions.push({
                    title: 'Deny',
                    icon: <DislikeOutlined style={{ color: 'rgba(250, 103, 92, 1)' }} />,
                    onClick: (record: IMarketAward) => showDenyModal(record),
                });
                actions.push({
                    title: 'Approve',
                    icon: <LikeOutlined style={{ color: 'rgba(72, 199, 116, 1)' }} />,
                    onClick: (record: IMarketAward) => showApproveModal(record),
                });
                break;
            case MarketAwardStatus.DENIED:
                if (isExpired) {
                    actions.push({
                        title: 'View',
                        icon: <EyeOutlined />,
                        onClick: (record: IMarketAward) => showViewModal(record),
                    });
                } else {
                    actions.push({
                        title: 'Approve',
                        icon: <LikeOutlined style={{ color: 'rgba(72, 199, 116, 1)' }} />,
                        onClick: (record: IMarketAward) => showApproveModal(record),
                    });
                }

                break;
            case MarketAwardStatus.AUTO:
            case MarketAwardStatus.APPROVED:
                if (isExpired) {
                    actions.push({
                        title: 'View',
                        icon: <EyeOutlined />,
                        onClick: (record: IMarketAward) => showViewModal(record),
                    });
                } else {
                    actions.push({
                        title: 'View',
                        icon: <EyeOutlined />,
                        onClick: (record: IMarketAward) => showViewModal(record),
                    });
                    actions.push({
                        title: 'View Event',
                        icon: <CalendarOutlined />,
                        onClick: (record: IMarketAward) => goToEvents(record),
                    });
                }

                break;
        }

        if (auth.user?.isAdminRoleType()) {
            actions.push({
                title: 'Audit Trail',
                onClick: (record: IMarketAward) => showAuditModal(record),
                icon: <HistoryOutlined />,
            });
        }

        return actions;
    };

    const tableColumns: CustomizableColumnType<IMarketAward>[] = [
        {
            title: 'Resource',
            dataIndex: 'resourceName',
            key: 'resourceName',
            minWidth: 250,
            fixed: 'left',
            render: (text, record) => record.resourceName || '',
            sorter: (a, b) => sortByField(a, b, 'resourceName'),
            ...getColumnSortOrder('resourceName', pageState.sorter),
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            width: 100,
            render: (text, record) => <MarketAwardSourceTag source={record.source} />,
            sorter: (a, b) => sortByField(a, b, 'source'),
            ...getColumnSortOrder('source', pageState.sorter),
        },
        {
            title: 'Start',
            dataIndex: 'start',
            key: 'start',
            width: 100,
            render: (text, record) => formatInTimeZone(record.start, CAISO_TZ, 'HH:mm:ss'),
            sorter: (a, b) => sortByField(a, b, 'start'),
            ...getColumnSortOrder('start', pageState.sorter),
        },
        {
            title: 'End',
            dataIndex: 'end',
            key: 'end',
            width: 100,
            render: (text, record) => formatInTimeZone(record.end, CAISO_TZ, 'HH:mm:ss'),
            sorter: (a, b) => sortByField(a, b, 'end'),
            ...getColumnSortOrder('end', pageState.sorter),
        },
        {
            title: '# of Sites',
            dataIndex: 'numOfSites',
            key: 'numOfSites',
            render: (text, record) => record.sites.length,
            sorter: (a, b) => sortByField(a, b, 'sites.length'),
            ...getColumnSortOrder('numOfSites', pageState.sorter),
        },
        {
            title: 'Awarded MW',
            dataIndex: 'mw',
            key: 'mw',
            render: (text, record) => record.mw,
            sorter: (a, b) => sortByField(a, b, 'mw'),
            ...getColumnSortOrder('mw', pageState.sorter),
        },
        {
            title: 'LMP',
            dataIndex: 'price',
            key: 'price',
            render: (text, record) => (record.price == null ? '-' : currencyUSDFormatter.format(record.price)),
            sorter: (a, b) => sortByField(a, b, 'price'),
            ...getColumnSortOrder('price', pageState.sorter),
        },
        {
            title: 'Earnings',
            dataIndex: 'earnings',
            key: 'earnings',
            render: (text, record) => (record.earnings == null ? '-' : currencyUSDFormatter.format(record.earnings)),
            sorter: (a, b) => sortByField(a, b, 'earnings'),
            ...getColumnSortOrder('earnings', pageState.sorter),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (text, record) => _.capitalize(record.status),
            sorter: (a, b) => sortByField(a, b, 'status'),
            ...getColumnSortOrder('status', pageState.sorter),
        },
        ...(ability.can('update', 'MarketAward')
            ? ([
                  {
                      key: 'action',
                      fixed: 'right',
                      minWidth: 60,
                      sorter: false,
                      render: (text, record) => <ActionList actions={actions(record)} item={record} />,
                  },
              ] as CustomizableColumnType<IMarketAward>[])
            : []),
    ];

    const mergedColumns = tableColumns.map((col, index) => ({
        ...col,
        onHeaderCell: (column: any) => ({
            width: column.width,
            columnIndex: index,
            style: { cursor: !!column.sorter ? 'pointer' : undefined },
            title: typeof column.title === 'function' ? String(column.title()) : column.title,
            onColumnResize: handleColumnResize,
        }),
    }));

    async function onTableChange(pagination: any, filter: any, sorter: any) {
        const preparedParams = toUsefulParams({ pagination, sorter });
        setPageQuery({ ...pageState, ...preparedParams });
    }

    const onSelectRow = (selectedRows: Key[]) => {
        handleSelectedItems(selectedRows as number[], data as any[]);
    };

    function onModalClose(actionTaken?: boolean) {
        if (actionTaken) {
            handleSelectedItems([], []);
        }

        dispatch({ type: 'CLOSE' });
    }

    function canDenySelected(selectedItems: IMarketAward[]) {
        return selectedItems.filter(isNotDeniedMarketAward).length > 0;
    }

    const { sidebarCollapsed, setSidebarCollapsed, layoutPaddingLeft } = useCmriOverviewSidebar();

    return (
        <div className="cmri-overview-view">
            <CustomizableTableContextProvider configKey="cmriOverview" columns={mergedColumns}>
                <Layout style={{ height: '100%', paddingLeft: layoutPaddingLeft }}>
                    <CmriOverviewSidebar
                        collapsed={sidebarCollapsed}
                        onCollapseChange={(collapsed: boolean) => setSidebarCollapsed(collapsed)}
                        totalRecords={marketAwards?.length || 0}
                        onFilterChange={() => {}}
                        gridComponent={<SidebarGrid />}
                    />
                    <Layout.Content style={{ padding: '0 26px' }}>
                        <PageHeader
                            pageTitle={<CaisoBreadcrumbNav />}
                            actions={[
                                <Button key="cmri-deny" disabled={!canDenySelected(selectedItems)} onClick={onDenySelected}>
                                    Deny
                                </Button>,
                                <CombineAndApproveButton selectedItems={selectedItems} onClick={onCombineAndApproveSelected} />,
                            ]}
                        />
                        <div>
                            <MarketAwardsDaysSelector
                                key="market-awards-days-selector"
                                value={activeDay}
                                counters={counters}
                                onChange={onDayChange}
                            />
                            <WithEmptyDataTable condition={isFetching}>
                                <CustomizableTable<IMarketAward>
                                    key="market-awards-table"
                                    size="small"
                                    rowKey="id"
                                    sticky={{ offsetHeader: 62 }}
                                    showHiddenColumnsCount={true}
                                    dataSource={marketAwards}
                                    loading={isFetching}
                                    onChange={onTableChange}
                                    rowSelection={
                                        ability.can('update', 'MarketAward') && activeDay !== 'yesterday'
                                            ? {
                                                  selectedRowKeys: selectedKeys,
                                                  preserveSelectedRowKeys: true,
                                                  onChange: onSelectRow,
                                                  getCheckboxProps: (record: IMarketAward) => {
                                                      return {
                                                          disabled: isAllowedToSelectRow(record),
                                                      };
                                                  },
                                              }
                                            : undefined
                                    }
                                    meta={{
                                        total: marketAwards.length,
                                        limit: pageState?.pagination?.pageSize || 10,
                                        offset: pageState?.pagination?.current
                                            ? (pageState.pagination.current - 1) * (pageState?.pagination?.pageSize || 10)
                                            : 0,
                                    }}
                                />
                            </WithEmptyDataTable>

                            {modalState.modal === marketAwardsModal.DENY && (
                                <MarketAwardsDenyModal key="market-awards-deny-modal" onClose={onModalClose} awards={modalState.targets} />
                            )}
                            {modalState.modal === marketAwardsModal.VIEW && (
                                <MarketAwardsViewModal key="market-awards-view-modal" onClose={onModalClose} awards={modalState.targets} />
                            )}
                            {modalState.modal === marketAwardsModal.APPROVE && (
                                <MarketAwardsViewModal key="market-awards-view-modal" onClose={onModalClose} awards={modalState.targets} />
                            )}
                            {modalState.modal === marketAwardsModal.AUDIT && (
                                <AuditTrailModal
                                    onClose={onModalClose}
                                    entityId={modalState.targets[0].id!}
                                    entityType={AuditEntityType.MARKET_AWARD}
                                />
                            )}
                        </div>
                    </Layout.Content>
                </Layout>
            </CustomizableTableContextProvider>
        </div>
    );
};

function CombineAndApproveButton({ selectedItems, onClick }: { selectedItems: IMarketAward[]; onClick: () => void }) {
    const [can, error] = canCombineAndApprove(selectedItems);

    return (
        <Tooltip title={can ? 'One event for multiple events' : error.message}>
            <Button key="cmri-combine-and-approve" title="One event for multiple events" disabled={!can} onClick={onClick}>
                Combine and Approve
            </Button>
        </Tooltip>
    );
}

function isAllowedToSelectRow(record: IMarketAward) {
    return [MarketAwardStatus.APPROVED, MarketAwardStatus.AUTO, MarketAwardStatus.IGNORED].includes(record.status);
}

function isNotDeniedMarketAward(item: IMarketAward) {
    return item.status !== MarketAwardStatus.DENIED;
}

function getActiveDay(val: string | undefined): DayValue {
    if (!val) return 'today';

    if (['yesterday', 'today', 'tomorrow'].includes(val)) {
        return val as DayValue;
    }

    return 'today';
}

function getDaysCounters(data: IMarketAward[] | undefined, days: Record<DayValue, { start: Date; end: Date }>, pageState: Record<string, string>) {
    if (!data) {
        return {
            yesterday: 0,
            today: 0,
            tomorrow: 0,
        };
    }

    const isNew = (marketAward: IMarketAward) => marketAward.status === MarketAwardStatus.NEW;

    const filterParams = {
        ...(pageState.resource && { resource: pageState.resource.split(',') }),
        ...(pageState.status && { status: pageState.status.split(',') }),
        ...(pageState.source && { source: pageState.source.split(',') }),
    };

    const yesterday = applyFilter(data, { dateRange: days['yesterday'], ...filterParams }).filter(isNew).length;
    const today = applyFilter(data, { dateRange: days['today'], ...filterParams }).filter(isNew).length;
    const tomorrow = applyFilter(data, { dateRange: days['tomorrow'], ...filterParams }).filter(isNew).length;

    return {
        yesterday,
        today,
        tomorrow,
    };
}

function applyFilter(
    items: IMarketAward[] | undefined,
    filter: {
        dateRange?: { start: Date; end: Date };
        source?: string[];
        resource?: string[];
        status?: string[];
        hoursRange?: [number, number];
        mw?: [number, number];
        price?: [number, number];
        earnings?: [number, number];
    }
) {
    if (!items) return [];

    const result = items.filter(item => {
        let allow = true;
        if (filter?.dateRange) {
            const inRange =
                new Date(item.start).valueOf() <= filter.dateRange.end.valueOf() && new Date(item.end).valueOf() > filter.dateRange.start.valueOf();

            allow = inRange;
            if (!allow) return false;
        }

        if (filter.resource) {
            allow = filter.resource.includes(item.resourceName);
            if (!allow) return false;
        }

        if (filter.source) {
            allow = filter.source.includes(item.source);
            if (!allow) return false;
        }

        if (filter.status) {
            allow = filter.status.includes(item.status);
            if (!allow) return false;
        }

        if (filter.hoursRange) {
            const [minHour, maxHour] = filter.hoursRange;
            const minMinutes = minHour * 60;
            const maxMinutes = maxHour * 60;

            const startMinutesInTz = Number(formatInTimeZone(item.start, CAISO_TZ, 'HH')) * 60 + Number(formatInTimeZone(item.start, CAISO_TZ, 'mm'));
            const endMinutesInTz = Number(formatInTimeZone(item.end, CAISO_TZ, 'HH')) * 60 + Number(formatInTimeZone(item.end, CAISO_TZ, 'mm'));

            allow = startMinutesInTz < maxMinutes && endMinutesInTz > minMinutes;
            if (!allow) return false;
        }

        if (filter.mw) {
            const [min, max] = filter.mw;
            allow = item.mw >= min && item.mw <= max;
            if (!allow) return false;
        }

        if (filter.price) {
            const [min, max] = filter.price;
            allow = item.price == null || (item.price >= min && item.price <= max);
            if (!allow) return false;
        }

        if (filter.earnings) {
            const [min, max] = filter.earnings;
            allow = item.earnings == null || (item.earnings >= min && item.earnings <= max);
            if (!allow) return false;
        }

        return allow;
    });

    return result;
}

type DayValue = 'yesterday' | 'today' | 'tomorrow';

function getDateRanges(): Record<DayValue, { start: Date; end: Date }> {
    const now = new Date();
    const todayDateCa = formatInTimeZone(now, CAISO_TZ, 'yyyy-MM-dd');
    const todayDateTimeCa = `${todayDateCa}T00:00:00`;
    const todayCaInUtc = fromZonedTime(todayDateTimeCa, CAISO_TZ);

    const result: Record<DayValue, { start: Date; end: Date }> = {
        yesterday: getYesterdayDateRange(todayCaInUtc),
        today: getTodayDateRange(todayCaInUtc),
        tomorrow: getTomorrowDateRange(todayCaInUtc),
    };

    return result;
}

function getYesterdayDateRange(todayCaInUtc: Date) {
    const start = sub(todayCaInUtc, { hours: 24 });
    const end = sub(add(start, { hours: 24 }), { seconds: 1 });

    return { start, end };
}

function getTodayDateRange(todayCaInUtc: Date) {
    const start = todayCaInUtc;
    const end = sub(add(start, { hours: 24 }), { seconds: 1 });

    return { start, end };
}

function getTomorrowDateRange(todayCaInUtc: Date) {
    const start = add(todayCaInUtc, { hours: 24 });
    const end = sub(add(start, { hours: 24 }), { seconds: 1 });

    return { start, end };
}
